import React from 'react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy = () => {
  return (
    <div className="w-full py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors mb-8"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>

        {/* Page Title */}
        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
          Privacy Policy
        </h1>
        
        <p className="text-gray-600 dark:text-gray-400 mb-8 transition-colors">
          Last Updated: January 21, 2026
        </p>

        {/* Introduction */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            1. Introduction
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            Welcome to The Jabari Holdings LLC ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            2. Information We Collect
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            We may collect the following types of information:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li><strong>Personal Information:</strong> Name, email address, and other contact details you provide when signing up or contacting us.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and actions taken.</li>
            <li><strong>Device Information:</strong> Information about your device, browser type, IP address, and operating system.</li>
            <li><strong>Content Data:</strong> Information you choose to store, organize, or process using our services.</li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            3. How We Use Your Information
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            We use the information we collect to:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Respond to comments, questions, and requests</li>
            <li>Send you technical notices, updates, security alerts, and support messages</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
            <li>Personalize and improve your experience</li>
            <li>Develop new products and services</li>
          </ul>
          
          <div className="mt-6">
            <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors">
              Email Communications and Marketing
            </h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
              By creating an account or providing your email address, you agree that ReelTravel may use your email to send you updates, promotional materials, and other marketing communications related to our Services. You can opt out of marketing emails at any time by clicking the "unsubscribe" link in any email or by contacting us directly.
            </p>
          </div>
        </section>

        {/* Data Sharing and Disclosure */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            4. Data Sharing and Disclosure
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            We may share your information with:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li><strong>Service Providers:</strong> Third-party vendors who provide services on our behalf</li>
            <li><strong>Business Partners:</strong> Companies we partner with to offer joint services</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          </ul>
        </section>

        {/* Your Rights */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            5. Your Rights and Choices
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            Depending on your location, you may have certain rights regarding your personal information, including:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li>Accessing, correcting, or deleting your personal information</li>
            <li>Objecting to our processing of your data</li>
            <li>Requesting restriction of processing</li>
            <li>Data portability</li>
            <li>Withdrawing consent</li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            6. Data Security
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
          </p>
        </section>

        {/* International Data Transfers */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            7. International Data Transfers
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            Your information may be transferred to and processed in countries other than the one in which you reside. These countries may have different data protection laws than your country of residence.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            8. Children's Privacy
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
          </p>
        </section>

        {/* Changes to This Privacy Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            9. Changes to This Privacy Policy
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </p>
        </section>

        {/* Contact Us */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            10. Contact Us
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <div className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            <p className="font-semibold">JABARI HOLDINGS LLC</p>
            <p>Email: <a href="mailto:ferasjabari@hotmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">ferasjabari@hotmail.com</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};
